import { z } from "zod";
import type { EventQualifyingStandard } from "./competition/reference";
import type { CompetitorType, EventGender } from "./competition/vocab";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "./pagination";

/**
 * M7b Sport entries (migration 0038, user decisions 2026-10-19; revamped by
 * 0062, user decisions 2026-10-29). Wire format: snake_case. The chain, per
 * NOC (responsible organisation) per games:
 *
 *   EOI  Expression of interest — which SPORTS, optionally which EVENTS of
 *        them, and optional estimates: per sport athletes men / women,
 *        officials (and medals while no event is picked); per picked event
 *        expected entries and gold / silver / bronze. Every number may be
 *        NULL = "not given" (distinct from 0). A sport with no event picked
 *        = interested in ALL its events.
 *   EBN  Entry by number — per EVENT, how many entries (INDIVIDUAL =
 *        athletes, TEAM = teams), capped by the event's `max_entries_per_noc`
 *        (DB guard → 422 ENTRY_OVER_CAP). EBN lists EVERY sport of the games
 *        (a "second chance"): entering an event of a sport outside the EOI
 *        adds that sport to the EOI (blank estimates) in the same statement.
 *   then the Shortlist (M7a) names the people. EBN numbers are SHOWN there
 *   (`declared_entries`), they never change `shortlistPrimaryLimit`.
 *
 * Enforced by the database, not the browser:
 *   · an EBN row's sport must be one of the organisation's EOI sports —
 *     composite FK `ebn_entries (games, org, sport) → eoi_sports` (→ 422
 *     ENTRY_SPORT_NOT_IN_EOI), ON DELETE CASCADE: removing a sport from the
 *     EOI deletes its EBN rows (and its EOI event picks) in the same statement;
 *   · an EOI event pick (`eoi_events`) hangs off its EOI sport the same way,
 *     and `fn_eoi_event_guard` checks the event is of the sport
 *     (ENTRY_EVENT_NOT_IN_SPORT), expected entries ≤ `max_entries_per_noc`
 *     (ENTRY_EVENT_OVER_CAP) and medals within `eventMedalCap`
 *     (ENTRY_MEDALS_OVER_CAP);
 *   · the event must be of that sport (`fn_ebn_entry_guard`, 23514
 *     `ck_ebn_entries_event_sport` → 422 ENTRY_EVENT_NOT_IN_SPORT), and
 *     entries ≤ `max_entries_per_noc` when set (`ck_ebn_entries_over_cap`).
 *
 * Decided by the writing statement (they depend on the caller's role):
 *   · scoped logins (NOC / host group) need `competition.declare` with a
 *     WRITABLE scope row over the organisation × games;
 *   · a window covering now for (games, phase, the organisation or every
 *     organisation) — no row = closed (409 ENTRY_WINDOW_CLOSED);
 *   · not FINAL (409 ENTRY_FINAL_LOCKED);
 *   · EBN only once the EOI is FINAL (409 EOI_NOT_FINAL).
 *   ADMIN / SUPER_ADMIN pass windows and finals. Only SUPER_ADMIN reopens;
 *   reopening the EOI reopens a FINAL EBN in the same statement.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

export const ENTRY_PHASES = ["EOI", "EBN"] as const;
export type EntryPhase = (typeof ENTRY_PHASES)[number];

/**
 * The phases an `entry_windows` row may be for (0072, user 2026-10-31): the
 * two entries phases, plus SHORTLIST — the shortlist's DEADLINE. SHORTLIST is
 * a window phase only (never an `entry_submissions` phase), and it reads the
 * other way round: no matching SHORTLIST window = open with no deadline
 * (`registration_windows`' rule); EOI / EBN with no window are closed.
 */
export const ENTRY_WINDOW_PHASES = [...ENTRY_PHASES, "SHORTLIST"] as const;
export type EntryWindowPhase = (typeof ENTRY_WINDOW_PHASES)[number];

/**
 * A phase's state for one (games, organisation), derived — never stored as a word:
 *   NOT_STARTED  no rows and never submitted;
 *   OPEN         rows, never submitted;
 *   FINAL        submitted, not reopened since — scoped logins are locked;
 *   REOPENED     a SUPER_ADMIN reopened it; it may be edited and submitted again.
 */
export const ENTRY_STATES = ["NOT_STARTED", "OPEN", "FINAL", "REOPENED"] as const;
export type EntryState = (typeof ENTRY_STATES)[number];

/** Each EOI estimate is NULL (not given) or 0..ENTRY_ESTIMATE_MAX (CHECK in the database). */
export const ENTRY_ESTIMATE_MAX = 999;
/** An EBN number is 1..ENTRY_NUMBER_MAX (CHECK); no row = not entering the event. */
export const ENTRY_NUMBER_MAX = 99;
/** `PUT /entries/eoi`: sports per call (a games has a few dozen). */
export const ENTRY_SPORTS_MAX = 100;
/** `PUT /entries/ebn`: events per call (one sport); `PUT /entries/eoi`: event picks per sport. */
export const ENTRY_EVENTS_MAX = 300;
/** `PUT /entries/eoi`: event picks per call, over every sport (a programme has a few hundred events). */
export const ENTRY_EOI_EVENTS_MAX = 1000;
/** `GET /entries/analytics?organization_ids=`: organisations per call. */
export const ENTRY_ANALYTICS_ORGS_MAX = 50;
/** `POST /entries/:phase/reopen`: the reason (audit headline). */
export const ENTRY_REASON_MAX = 500;
/** `GET /entries/organizations` and `/entries/matrix`: default page size (≤ PAGE_SIZE_MAX). */
export const ENTRY_PAGE_SIZE_DEFAULT = 50;

/** The six EOI numbers of one sport, in display order. */
export const EOI_ESTIMATE_FIELDS = ["est_athletes_men", "est_athletes_women", "est_officials", "expected_gold", "expected_silver", "expected_bronze"] as const;
export type EoiEstimateField = (typeof EOI_ESTIMATE_FIELDS)[number];
/** One sport's six numbers as stored: null = not given (0062). */
export type EoiEstimates = Record<EoiEstimateField, number | null>;
/** Sums over sports / organisations: a number not given counts 0. */
export type EoiEstimateTotals = Record<EoiEstimateField, number>;
/** The three medal fields (a sport's, an event pick's). */
export const EOI_MEDAL_FIELDS = ["expected_gold", "expected_silver", "expected_bronze"] as const;
export type EoiMedalField = (typeof EOI_MEDAL_FIELDS)[number];

/** The error codes a refused entries write answers (lib/errors.ts ENTRY_ERROR_STATUS). */
export const ENTRY_REFUSALS = [
	"ENTRY_WINDOW_CLOSED",
	"ENTRY_FINAL_LOCKED",
	"EOI_NOT_FINAL",
	"ENTRY_EMPTY",
	"ENTRY_OVER_CAP",
	"ENTRY_SPORT_NOT_IN_EOI",
	"ENTRY_EVENT_NOT_IN_SPORT",
	"ENTRY_EVENT_OVER_CAP",
	"ENTRY_MEDALS_OVER_CAP",
] as const;
export type EntryRefusal = (typeof ENTRY_REFUSALS)[number];

/** The 0038 constraints a psql / race write can hit, and the code each maps onto (kept equal to the migration by contract-parity.entries.test.ts). */
export const EBN_ENTRY_CONSTRAINTS = {
	ck_ebn_entries_event_sport: "ENTRY_EVENT_NOT_IN_SPORT",
	ck_ebn_entries_over_cap: "ENTRY_OVER_CAP",
	fk_ebn_entries_eoi_sport: "ENTRY_SPORT_NOT_IN_EOI",
} as const satisfies Record<string, EntryRefusal>;

/** The 0062 constraints on EOI event picks (`fn_eoi_event_guard`, the EOI FK), kept equal to the migration by contract-parity.entries.test.ts. */
export const EOI_EVENT_CONSTRAINTS = {
	ck_eoi_events_event_sport: "ENTRY_EVENT_NOT_IN_SPORT",
	ck_eoi_events_over_cap: "ENTRY_EVENT_OVER_CAP",
	ck_eoi_events_medals_over_cap: "ENTRY_MEDALS_OVER_CAP",
	fk_eoi_events_eoi_sport: "ENTRY_SPORT_NOT_IN_EOI",
} as const satisfies Record<string, EntryRefusal>;

/**
 * EBN warnings — shown before submit, never a block:
 *   SPORT_WITHOUT_EVENTS  an EOI sport with no EBN event;
 *   ABOVE_ESTIMATE        one event alone needs more athletes of a gender than
 *                         the EOI estimated (MEN → men, WOMEN → women, MIXED /
 *                         OPEN → men + women): the estimate is too low;
 *   BELOW_ESTIMATE        every entry of the sport together uses fewer athletes
 *                         than the EOI estimated (men + women): people the
 *                         EBN gives nothing to do;
 *   NOT_IN_EOI            (info) a sport outside the EOI with entries: saving
 *                         added / will add it to the EOI with blank estimates.
 * An estimate not given (null) is never compared: no ABOVE / BELOW from it
 * (MIXED / OPEN and BELOW need both men and women given). Sports outside the
 * EOI get only NOT_IN_EOI.
 */
export const EBN_WARNING_KINDS = ["SPORT_WITHOUT_EVENTS", "ABOVE_ESTIMATE", "BELOW_ESTIMATE", "NOT_IN_EOI"] as const;
export type EbnWarningKind = (typeof EBN_WARNING_KINDS)[number];

// ── Pure helpers (the API computes the same in SQL / with these) ───────────

export interface EntrySubmissionRow {
	submitted_at: string | null;
	reopened_at: string | null;
}

/** The phase state from its submission row (null = never submitted) and whether it has any rows. */
export function entryState(submission: EntrySubmissionRow | null, hasRows: boolean): EntryState {
	if (submission === null || submission.submitted_at === null) return hasRows ? "OPEN" : "NOT_STARTED";
	return submission.reopened_at === null ? "FINAL" : "REOPENED";
}

/** Whether a scoped login is locked out by the state (admins never are). */
export const entryStateLocked = (state: EntryState): boolean => state === "FINAL";

/**
 * How many athletes an EBN number implies: INDIVIDUAL = the number; TEAM =
 * teams × `team_size_max` (else `team_size_min`, else 1). The API's
 * `impliedAthletesSql` is the same rule.
 */
export function impliedAthletes(event: { competitor_type: CompetitorType; team_size_min: number | null; team_size_max: number | null }, entries: number): number {
	if (event.competitor_type === "INDIVIDUAL") return entries;
	return entries * (event.team_size_max ?? event.team_size_min ?? 1);
}

export type EbnWarning =
	| { kind: "SPORT_WITHOUT_EVENTS"; sport_id: string }
	| { kind: "ABOVE_ESTIMATE"; sport_id: string; event_id: string; gender: EventGender; needed: number; estimated: number }
	| { kind: "BELOW_ESTIMATE"; sport_id: string; implied_athletes: number; estimated: number }
	| { kind: "NOT_IN_EOI"; sport_id: string };

/** The input `ebnWarnings` reads: an EBN view's sports (the subset of fields it needs). */
export interface EbnWarningSport {
	sport_id: string;
	/** The sport is in the organisation's EOI (an `eoi_sports` row). */
	in_eoi: boolean;
	eoi: Pick<EoiEstimates, "est_athletes_men" | "est_athletes_women">;
	disciplines: ReadonlyArray<{
		events: ReadonlyArray<{
			event_id: string;
			gender: EventGender;
			competitor_type: CompetitorType;
			team_size_min: number | null;
			team_size_max: number | null;
			entries: number | null;
		}>;
	}>;
}

/** The EBN warnings, in sport order, then event order (see EBN_WARNING_KINDS). Pure: the API sends exactly this. */
export function ebnWarnings(sports: readonly EbnWarningSport[]): EbnWarning[] {
	const out: EbnWarning[] = [];
	for (const s of sports) {
		const men = s.eoi.est_athletes_men;
		const women = s.eoi.est_athletes_women;
		const both = men !== null && women !== null ? men + women : null;
		let total = 0;
		let any = false;
		const above: EbnWarning[] = [];
		for (const d of s.disciplines) {
			for (const e of d.events) {
				if (e.entries === null || e.entries <= 0) continue;
				any = true;
				const needed = impliedAthletes(e, e.entries);
				total += needed;
				const estimated = e.gender === "MEN" ? men : e.gender === "WOMEN" ? women : both;
				if (estimated !== null && needed > estimated) {
					above.push({ kind: "ABOVE_ESTIMATE", sport_id: s.sport_id, event_id: e.event_id, gender: e.gender, needed, estimated });
				}
			}
		}
		if (!s.in_eoi) {
			if (any) out.push({ kind: "NOT_IN_EOI", sport_id: s.sport_id });
			continue;
		}
		out.push(...above);
		if (!any) out.push({ kind: "SPORT_WITHOUT_EVENTS", sport_id: s.sport_id });
		else if (both !== null && total < both) out.push({ kind: "BELOW_ESTIMATE", sport_id: s.sport_id, implied_athletes: total, estimated: both });
	}
	return out;
}

/** What an event allows one organisation to win, per colour and in total. */
export interface EventMedalCap {
	gold: number;
	silver: number;
	bronze: number;
	total: number;
}

/** The event fields `eventMedalCap` reads. */
export interface EventMedalCapInput {
	is_medal_event: boolean;
	gold_count: number;
	silver_count: number;
	bronze_count: number;
	max_entries_per_noc: number | null;
}

/**
 * The most medals one organisation can expect from an event (0062;
 * `fn_eoi_event_guard` is the same rule in SQL, pinned by a unit test):
 *   n      = `entries` (its expected entries — a TEAM event counts teams, 1
 *            entry per team), else `max_entries_per_noc`, else the event's
 *            medal total;
 *   colour ≤ min(n, the event's count of that colour);
 *   total  ≤ min(n, gold + silver + bronze).
 * A non-medal event (`is_medal_event` false) allows none.
 */
export function eventMedalCap(event: EventMedalCapInput, entries: number | null): EventMedalCap {
	if (!event.is_medal_event) return { gold: 0, silver: 0, bronze: 0, total: 0 };
	const offered = event.gold_count + event.silver_count + event.bronze_count;
	const n = entries ?? event.max_entries_per_noc ?? offered;
	return {
		gold: Math.min(n, event.gold_count),
		silver: Math.min(n, event.silver_count),
		bronze: Math.min(n, event.bronze_count),
		total: Math.min(n, offered),
	};
}

/** Whether expected medals (null / missing = not given = 0) fit `eventMedalCap`. */
export function medalsWithinCap(cap: EventMedalCap, medals: Partial<Record<EoiMedalField, number | null>>): boolean {
	const g = medals.expected_gold ?? 0;
	const s = medals.expected_silver ?? 0;
	const b = medals.expected_bronze ?? 0;
	return g <= cap.gold && s <= cap.silver && b <= cap.bronze && g + s + b <= cap.total;
}

/**
 * A sport's expected medals as every reader counts them (the API's
 * `eoiMedalsSql` is the same rule): with events picked, the sum of the picks'
 * medals per colour (null when no pick gave that colour); with none picked,
 * the sport's own numbers. (A write stores the sport's own as null once
 * events are picked.)
 */
export function eoiSportMedals(
	sport: Pick<EoiEstimates, EoiMedalField> & { events: ReadonlyArray<Pick<EoiEstimates, EoiMedalField>> },
): Record<EoiMedalField, number | null> {
	if (sport.events.length === 0) return { expected_gold: sport.expected_gold, expected_silver: sport.expected_silver, expected_bronze: sport.expected_bronze };
	const sum = (f: EoiMedalField) => sport.events.reduce<number | null>((n, e) => (e[f] === null ? n : (n ?? 0) + (e[f] as number)), null);
	return { expected_gold: sum("expected_gold"), expected_silver: sum("expected_silver"), expected_bronze: sum("expected_bronze") };
}

/** The facts of a picked event `eoiSportAthletes` reads, and the pick's expected entries. */
export interface EoiAthletePick {
	gender: EventGender;
	competitor_type: CompetitorType;
	team_size_min: number | null;
	team_size_max: number | null;
	est_entries: number | null;
}

/**
 * A sport's estimated athletes as every reader counts them (the API's
 * `eoiMedalsSql` is the same rule), per gender: the sport's own number when
 * given; otherwise what the picked events' expected entries imply
 * (`impliedAthletes`) over the picks of that gender — MEN → men, WOMEN →
 * women — null when no such pick gave a number. MIXED / OPEN picks name no
 * gender and are never counted. An NOC that gave "2 expected entries" in
 * Men's Recurve and left the sport's boxes blank thus shows 2 men, not 0.
 */
export function eoiSportAthletes(
	sport: Pick<EoiEstimates, "est_athletes_men" | "est_athletes_women"> & { events: ReadonlyArray<EoiAthletePick> },
): Pick<EoiEstimates, "est_athletes_men" | "est_athletes_women"> {
	const implied = (gender: EventGender) =>
		sport.events.reduce<number | null>((n, e) => (e.gender !== gender || e.est_entries === null ? n : (n ?? 0) + impliedAthletes(e, e.est_entries)), null);
	return {
		est_athletes_men: sport.est_athletes_men ?? implied("MEN"),
		est_athletes_women: sport.est_athletes_women ?? implied("WOMEN"),
	};
}

// ── Queries ─────────────────────────────────────────────────────────────────

const uuid = z.string().uuid();
const estimate = z.number().int().min(0).max(ENTRY_ESTIMATE_MAX);
/** A comma-separated list of ids, 1..max: deduplicated, lower-cased. */
const uuidList = (max: number) =>
	z
		.string()
		.trim()
		.min(1)
		.transform((v) => [...new Set(v.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean))])
		.pipe(z.array(uuid).min(1).max(max));

/**
 * `GET /entries/organizations` sort keys, ordered in SQL. A state sorts by
 * progress (FINAL, REOPENED, OPEN, NOT_STARTED); medals by gold, then silver,
 * then bronze (the medal table's order); `athletes` = estimated men + women;
 * `entries` = Σ EBN numbers. Ties fall back to the name.
 */
export const ENTRY_ORG_SORTS = ["name", "eoi_state", "ebn_state", "sports", "athletes", "medals", "entries"] as const;
export type EntryOrgSort = (typeof ENTRY_ORG_SORTS)[number];
/** The direction a sort key starts in: names A→Z, everything else most first. */
export const entryOrgSortDefaultDir = (sort: EntryOrgSort): "asc" | "desc" => (sort === "name" ? "asc" : "desc");

/** `GET /entries/organizations` — the organisations the caller may act for, offset paged (a NOC list is small). */
export const entryOrganizationsQuery = paginationQuery.extend({
	games_id: uuid,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(ENTRY_PAGE_SIZE_DEFAULT),
	/** Organisation name or country code. */
	search: searchParam.optional(),
	sort: z.enum(ENTRY_ORG_SORTS).default("name"),
	/** Omitted = the key's own direction (`entryOrgSortDefaultDir`). */
	dir: z.enum(["asc", "desc"]).optional(),
});
export type EntryOrganizationsQuery = z.infer<typeof entryOrganizationsQuery>;
export type EntryOrganizationsQueryInput = z.input<typeof entryOrganizationsQuery>;

/**
 * `GET /entries/eoi`, `GET /entries/ebn`. A scoped login may omit
 * `organization_id` when its scope names exactly one organisation; ADMIN /
 * SUPER_ADMIN must name one (422). Another NOC's organisation is 404.
 */
export const entryOrgQuery = z.object({
	games_id: uuid,
	organization_id: uuid.optional(),
});
export type EntryOrgQuery = z.infer<typeof entryOrgQuery>;
export type EntryOrgQueryInput = z.input<typeof entryOrgQuery>;

/** `GET /entries/eoi/remove-impact?sport_ids=a,b` — comma-separated, 1..ENTRY_SPORTS_MAX. */
export const eoiRemoveImpactQuery = entryOrgQuery.extend({
	sport_ids: uuidList(ENTRY_SPORTS_MAX),
});
export type EoiRemoveImpactQuery = z.infer<typeof eoiRemoveImpactQuery>;

/** `GET /entries/matrix` (admins) — organisations × sports, organisations offset paged. */
export const entryMatrixQuery = paginationQuery.extend({
	games_id: uuid,
	phase: z.enum(ENTRY_PHASES),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(ENTRY_PAGE_SIZE_DEFAULT),
});
export type EntryMatrixQuery = z.infer<typeof entryMatrixQuery>;
export type EntryMatrixQueryInput = z.input<typeof entryMatrixQuery>;

/** `GET /entries/by-event` (admins) — one sport's events × organisations. */
export const entryByEventQuery = z.object({ games_id: uuid, sport_id: uuid });
export type EntryByEventQuery = z.infer<typeof entryByEventQuery>;

/** `GET /entries/export.csv` (admins). */
export const entryExportQuery = z.object({ games_id: uuid, phase: z.enum(ENTRY_PHASES) });
export type EntryExportQuery = z.infer<typeof entryExportQuery>;

export const entryPhaseParams = z.object({ phase: z.enum(ENTRY_PHASES) });

/**
 * `GET /entries/analytics` (admins) — the dashboard, ONE statement.
 * `organization_ids` (comma list) narrows to those organisations;
 * `sport_id` narrows every section except `by_sport` (the chart a sport is
 * picked from) and `timeline` (a submission is the whole phase), and adds
 * `by_event`.
 */
export const entryAnalyticsQuery = z.object({
	games_id: uuid,
	organization_ids: uuidList(ENTRY_ANALYTICS_ORGS_MAX).optional(),
	sport_id: uuid.optional(),
});
export type EntryAnalyticsQuery = z.infer<typeof entryAnalyticsQuery>;
export type EntryAnalyticsQueryInput = z.input<typeof entryAnalyticsQuery>;

// ── Bodies ──────────────────────────────────────────────────────────────────

const pickEntries = z.number().int().min(1).max(ENTRY_NUMBER_MAX).nullable().default(null);
const pickMedals = z.number().int().min(0).max(ENTRY_NUMBER_MAX).nullable().default(null);

/**
 * One picked event of an EOI sport (0062). Every number optional (null /
 * omitted = not given): expected entries 1..ENTRY_NUMBER_MAX and ≤ the
 * event's `max_entries_per_noc` (422 ENTRY_EVENT_OVER_CAP); medals within
 * `eventMedalCap` (422 ENTRY_MEDALS_OVER_CAP).
 */
export const eoiEventInput = z.object({
	event_id: uuid,
	est_entries: pickEntries,
	expected_gold: pickMedals,
	expected_silver: pickMedals,
	expected_bronze: pickMedals,
});
export type EoiEventInput = z.input<typeof eoiEventInput>;

/**
 * One EOI sport. Every estimate optional (null / omitted = not given).
 * `events` = the picked events of the sport (omitted / [] = interested in
 * ALL of them). With events picked, medals live on the events: the sport's
 * own `expected_*` are stored as null whatever is sent.
 */
export const eoiSportInput = z.object({
	sport_id: uuid,
	est_athletes_men: estimate.nullable().default(null),
	est_athletes_women: estimate.nullable().default(null),
	est_officials: estimate.nullable().default(null),
	expected_gold: estimate.nullable().default(null),
	expected_silver: estimate.nullable().default(null),
	expected_bronze: estimate.nullable().default(null),
	events: z
		.array(eoiEventInput)
		.max(ENTRY_EVENTS_MAX)
		.default([])
		.refine((rows) => new Set(rows.map((r) => r.event_id.toLowerCase())).size === rows.length, "Each event may appear once"),
});
export type EoiSportInput = z.input<typeof eoiSportInput>;

/**
 * `PUT /entries/eoi` — the FULL list of the organisation's EOI sports.
 * Missing sports are removed (their EBN rows go with them — ask
 * `/entries/eoi/remove-impact` first); unchanged rows write nothing.
 */
export const putEoiBody = z.object({
	games_id: uuid,
	organization_id: uuid,
	sports: z
		.array(eoiSportInput)
		.max(ENTRY_SPORTS_MAX)
		.refine((rows) => new Set(rows.map((r) => r.sport_id.toLowerCase())).size === rows.length, "Each sport may appear once")
		.refine((rows) => rows.reduce((n, r) => n + r.events.length, 0) <= ENTRY_EOI_EVENTS_MAX, `At most ${ENTRY_EOI_EVENTS_MAX} events in all`)
		.refine((rows) => {
			const ids = rows.flatMap((r) => r.events.map((e) => e.event_id.toLowerCase()));
			return new Set(ids).size === ids.length;
		}, "Each event may appear once"),
});
export type PutEoiBody = z.input<typeof putEoiBody>;

/**
 * `PUT /entries/ebn` — the FULL list of ONE sport's EBN events. Missing
 * events are removed; unchanged rows write nothing. An empty list clears
 * the sport. A sport outside the EOI is ADDED to it (blank estimates, no
 * event picks) in the same statement when the list has an entry
 * (`eoi_sport_added`); the EOI must still be FINAL.
 */
export const putEbnBody = z.object({
	games_id: uuid,
	organization_id: uuid,
	sport_id: uuid,
	entries: z
		.array(z.object({ event_id: uuid, entries: z.number().int().min(1).max(ENTRY_NUMBER_MAX) }))
		.max(ENTRY_EVENTS_MAX)
		.refine((rows) => new Set(rows.map((r) => r.event_id.toLowerCase())).size === rows.length, "Each event may appear once"),
});
export type PutEbnBody = z.input<typeof putEbnBody>;

/** `POST /entries/:phase/submit`. EOI needs ≥ 1 sport, EBN ≥ 1 entry (422 ENTRY_EMPTY); EBN needs the EOI FINAL. */
export const entryPhaseBody = z.object({ games_id: uuid, organization_id: uuid });
export type EntryPhaseBody = z.input<typeof entryPhaseBody>;

/** `POST /entries/:phase/reopen` — SUPER_ADMIN; `reason` is the audit headline. */
export const entryReopenBody = entryPhaseBody.extend({ reason: z.string().trim().min(1).max(ENTRY_REASON_MAX) });
export type EntryReopenBody = z.input<typeof entryReopenBody>;

// ── Entry windows ───────────────────────────────────────────────────────────

export interface EntryWindowFormMessages {
	required: string;
	dateTimeInvalid: string;
	closesAfterOpens: string;
}
export const enEntryWindowFormMessages: EntryWindowFormMessages = {
	required: "Required",
	dateTimeInvalid: "Enter a valid date and time",
	closesAfterOpens: "Must be after the opening time",
};

/**
 * `POST /entry-windows` — when scoped logins may write one phase for one
 * games, for one organisation (`organization_id`) or every organisation
 * (null). EOI / EBN: no row covering now = closed. SHORTLIST: the deadline
 * (`closes_at`); no matching row = open. SUPER_ADMIN writes, admins read.
 */
export function makeEntryWindowForm(m: EntryWindowFormMessages = enEntryWindowFormMessages) {
	return z
		.object({
			games_id: z.string({ required_error: m.required }).uuid(),
			phase: z.enum(ENTRY_WINDOW_PHASES, { required_error: m.required }),
			organization_id: z.string().uuid().nullable(),
			opens_at: z.string({ required_error: m.required }).trim().datetime({ offset: true, message: m.dateTimeInvalid }),
			closes_at: z.string({ required_error: m.required }).trim().datetime({ offset: true, message: m.dateTimeInvalid }),
		})
		.refine((v) => Date.parse(v.closes_at) > Date.parse(v.opens_at), { path: ["closes_at"], message: m.closesAfterOpens });
}
export const entryWindowBody = makeEntryWindowForm();
export type EntryWindowBody = z.input<typeof entryWindowBody>;

/** `PATCH /entry-windows/:id` — any subset; the server re-checks closes > opens against the stored row (CHECK). */
export function makePatchEntryWindowForm(m: EntryWindowFormMessages = enEntryWindowFormMessages) {
	return z
		.object({
			phase: z.enum(ENTRY_WINDOW_PHASES).optional(),
			organization_id: z.string().uuid().nullable().optional(),
			opens_at: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }).optional(),
			closes_at: z.string().trim().datetime({ offset: true, message: m.dateTimeInvalid }).optional(),
			reason: z.string().trim().min(1).max(ENTRY_REASON_MAX).optional(),
		})
		.refine((v) => v.opens_at === undefined || v.closes_at === undefined || Date.parse(v.closes_at) > Date.parse(v.opens_at), {
			path: ["closes_at"],
			message: m.closesAfterOpens,
		});
}
export const patchEntryWindowBody = makePatchEntryWindowForm();
export type PatchEntryWindowBody = z.input<typeof patchEntryWindowBody>;

/** `GET /entry-windows` (admins) — offset paged, a small table. */
export const entryWindowsQuery = paginationQuery.extend({
	games_id: uuid.optional(),
	phase: z.enum(ENTRY_WINDOW_PHASES).optional(),
	organization_id: uuid.optional(),
});
export type EntryWindowsQuery = z.infer<typeof entryWindowsQuery>;
export type EntryWindowsQueryInput = z.input<typeof entryWindowsQuery>;

// ── Responses ───────────────────────────────────────────────────────────────

export interface EntryUserRef {
	id: string;
	name: string;
}

export interface EntryOrgRef {
	id: string;
	name: string;
	/** The NOC's country code (IOC), when it has one. */
	code: string | null;
}

export interface EntrySportRef {
	sport_id: string;
	code: string;
	name: string;
}

/** One phase's submission for one (games, organisation). */
export interface EntrySubmission {
	state: EntryState;
	submitted_at: string | null;
	submitted_by: EntryUserRef | null;
	reopened_at: string | null;
	reopened_by: EntryUserRef | null;
}

/**
 * The window that applies to the organisation for a phase, by the database
 * clock. `open` = a window covers now, and `closes_at` is the latest end of
 * those. Not open: `closes_at` = when the latest window that already ended
 * closed (null if none ended), `next_opens_at` = the earliest window still
 * to open (null if none). Admins are
 * never bound by it (`can_edit` says what the caller may do).
 */
export interface EntryWindowStatus {
	open: boolean;
	closes_at: string | null;
	next_opens_at: string | null;
}

export interface EntryPhaseSummary {
	state: EntryState;
	submitted_at: string | null;
	window: EntryWindowStatus;
}

/** One row of `GET /entries/organizations` — ONE aggregate for the page. */
export interface EntryOrganizationRow {
	organization: EntryOrgRef;
	/** Sums (not given = 0); medals as `eoiSportMedals` counts them (event picks first). */
	eoi: EntryPhaseSummary &
		EoiEstimateTotals & {
			/** EOI sports. */
			sports: number;
		};
	ebn: EntryPhaseSummary & {
		/** Events with an EBN number. */
		events: number;
		/** Σ EBN numbers (athletes + teams). */
		entries: number;
		/** Σ `impliedAthletes`. */
		implied_athletes: number;
	};
}

/** Over every organisation the caller may act for (not just the page): the admin KPI row. */
export interface EntryOrganizationsSummary {
	organizations: number;
	eoi_started: number;
	eoi_final: number;
	ebn_started: number;
	ebn_final: number;
}

export interface EntryOrganizationsResponse {
	items: EntryOrganizationRow[];
	page: number;
	page_size: number;
	total: number;
	total_pages: number;
	summary: EntryOrganizationsSummary;
}

/** An event's numbers both editors show: the medals on offer and both quotas (0062). */
export interface EntryEventFacts {
	event_id: string;
	code: string;
	name: string;
	gender: EventGender;
	competitor_type: CompetitorType;
	team_size_min: number | null;
	team_size_max: number | null;
	/** Active event of an active discipline. Inactive ones show only when already picked / entered. */
	is_active: boolean;
	is_medal_event: boolean;
	gold_count: number;
	silver_count: number;
	bronze_count: number;
	/** Entries that compete (the EBN cap and the medal cap); null = no limit. */
	max_entries_per_noc: number | null;
	/** Athletes a NOC may shortlist as PRIMARY; null = falls back (`shortlistPrimaryLimit`). */
	max_shortlist_per_noc: number | null;
	/** The qualifying standard; null = none. */
	qualifying: EventQualifyingStandard | null;
}

/** An organisation's EOI pick of one event: every number null = not given. */
export interface EoiEventPick {
	est_entries: number | null;
	expected_gold: number | null;
	expected_silver: number | null;
	expected_bronze: number | null;
}

/** One event of an EOI sport: `selected` = picked (an `eoi_events` row); unpicked events carry nulls. */
export interface EoiEventRow extends EntryEventFacts, EoiEventPick {
	selected: boolean;
}

export interface EoiDisciplineRow {
	discipline_id: string;
	code: string;
	name: string;
	events: EoiEventRow[];
}

/**
 * One sport of `GET /entries/eoi`: every active sport of the games (and any
 * inactive one already chosen), with its disciplines → events (active ones,
 * and any already picked). Estimates null = not given (unselected sports:
 * all null). With events picked the sport's own `expected_*` are null —
 * medals live on the events (`eoiSportMedals`).
 */
export interface EoiSportRow extends EntrySportRef, EoiEstimates {
	is_active: boolean;
	/** The organisation chose this sport (an `eoi_sports` row). */
	selected: boolean;
	/** Picked events (0 = interested in all `events_total`). */
	events_selected: number;
	events_total: number;
	/** EBN events / Σ numbers under this sport — what removing it would delete. */
	ebn_events: number;
	ebn_entries: number;
	disciplines: EoiDisciplineRow[];
}

/** `GET /entries/eoi` — ONE statement. */
export interface EoiView {
	games_id: string;
	organization: EntryOrgRef;
	submission: EntrySubmission;
	window: EntryWindowStatus;
	/** The caller may change rows now (scope + permission + window + not FINAL; admins always). */
	can_edit: boolean;
	/** The caller may submit now (as `can_edit`, the state is not FINAL, and at least one sport is chosen). */
	can_submit: boolean;
	ebn_state: EntryState;
	sports: EoiSportRow[];
	/** Over the selected sports (not given = 0; medals by `eoiSportMedals`); `events` = picked events. */
	totals: EoiEstimateTotals & { sports: number; events: number };
}

/** `PUT /entries/eoi`. Sports and event picks are diffed alike. */
export interface EoiWriteResult {
	added: number;
	updated: number;
	removed: number;
	/** Rows that already said that (nothing written, no audit row). */
	unchanged: number;
	/** EBN rows the removed sports took with them (FK cascade). */
	ebn_entries_removed: number;
	/** Event picks inserted / changed / removed (a removed sport's picks go with it, not counted here) / unchanged. */
	events_added: number;
	events_updated: number;
	events_removed: number;
	events_unchanged: number;
}

/** `GET /entries/eoi/remove-impact` — what `PUT /entries/eoi` would cascade if these sports went. */
export interface EoiRemoveImpact {
	items: Array<{ sport_id: string; ebn_events: number; ebn_entries: number }>;
	ebn_events: number;
}

export interface EbnEventRow extends EntryEventFacts {
	/** The organisation's EBN number; null = not entering. */
	entries: number | null;
	/** `impliedAthletes(event, entries ?? 0)`. */
	implied_athletes: number;
	/** The organisation's EOI pick of this event ("you estimated N"); null = not picked. */
	eoi_event: EoiEventPick | null;
}

export interface EbnDisciplineRow {
	discipline_id: string;
	code: string;
	name: string;
	events: EbnEventRow[];
}

export interface EbnSportRow extends EntrySportRef {
	is_active: boolean;
	/** The sport is in the organisation's EOI. False = "other sports": an entry here adds it to the EOI on save. */
	in_eoi: boolean;
	/** The EOI numbers (all null when not in the EOI, or not given). */
	eoi: EoiEstimates;
	/** EOI event picks under this sport (0 with `in_eoi` = interested in all its events). */
	eoi_events_selected: number;
	events: number;
	entries: number;
	implied_athletes: number;
	disciplines: EbnDisciplineRow[];
}

/**
 * `GET /entries/ebn` — ONE statement: EVERY active sport of the games (and
 * any inactive one in the EOI or entered) → active disciplines → active
 * events (+ any already entered), each with the EOI pick.
 */
export interface EbnView {
	games_id: string;
	organization: EntryOrgRef;
	submission: EntrySubmission;
	window: EntryWindowStatus;
	eoi_state: EntryState;
	/** The caller may change rows now (EOI FINAL, scope + permission + window + not FINAL; admins: EOI FINAL). */
	can_edit: boolean;
	/** As `can_edit`, the state is not FINAL, and at least one event has a number. */
	can_submit: boolean;
	sports: EbnSportRow[];
	warnings: EbnWarning[];
}

/** `PUT /entries/ebn`. */
export interface EbnWriteResult {
	added: number;
	updated: number;
	removed: number;
	unchanged: number;
	/** The sport was not in the EOI and was added to it (blank estimates) by this write. */
	eoi_sport_added: boolean;
}

/** `POST /entries/:phase/submit` and `/reopen`. */
export interface EntrySubmitResult {
	phase: EntryPhase;
	submission: EntrySubmission;
	/** EOI reopen: the EBN was FINAL and was reopened in the same statement. */
	ebn_reopened?: boolean;
}

/**
 * One cell of the matrix: an (organisation, sport) with an EOI row. Estimates
 * null = not given; medals as `eoiSportMedals` (event picks first). EBN
 * fields are 0 when the sport has no EBN rows.
 */
export interface EntryMatrixCell extends EoiEstimates {
	sport_id: string;
	ebn_events: number;
	ebn_entries: number;
	implied_athletes: number;
}

export interface EntryMatrixRow {
	organization: EntryOrgRef;
	/** The state of the phase asked for. */
	state: EntryState;
	/** Only the sports with an EOI row (phase EOI) / with EBN rows (phase EBN). */
	cells: EntryMatrixCell[];
}

/** `GET /entries/matrix` — ONE aggregate. Organisations are paged; `sports` = every sport of the games with at least one cell. */
export interface EntryMatrix {
	games_id: string;
	phase: EntryPhase;
	sports: EntrySportRef[];
	items: EntryMatrixRow[];
	page: number;
	page_size: number;
	total: number;
	total_pages: number;
}

export interface EntryByEventRow {
	event_id: string;
	code: string;
	name: string;
	discipline: { discipline_id: string; code: string; name: string };
	gender: EventGender;
	competitor_type: CompetitorType;
	team_size_min: number | null;
	team_size_max: number | null;
	max_entries_per_noc: number | null;
	/** Σ over organisations. */
	total_entries: number;
	implied_athletes: number;
	/** One per organisation that entered it. */
	entries: Array<{ organization_id: string; entries: number }>;
}

/** `GET /entries/by-event` — ONE statement. `organizations` = those with the sport in their EOI. */
export interface EntryByEvent {
	games_id: string;
	sport: EntrySportRef;
	organizations: Array<EntryOrgRef & { eoi_state: EntryState; ebn_state: EntryState }>;
	events: EntryByEventRow[];
}

export interface EntryWindow {
	entry_window_id: string;
	games: { id: string; code: string };
	phase: EntryWindowPhase;
	/** null = every organisation. */
	organization: { id: string; name: string } | null;
	opens_at: string;
	closes_at: string;
	/** `now()` is inside [opens_at, closes_at], by the database clock. */
	is_open: boolean;
	created_at: string;
	updated_at: string;
}

// ── Analytics (admins) ──────────────────────────────────────────────────────

/** Medals the selected events award (Σ of their counts; active medal events only). */
export interface EntryMedalsOnOffer {
	gold: number;
	silver: number;
	bronze: number;
}

/**
 * The dashboard's headline numbers. "Interest" counts a sport in an EOI; an
 * event counts when picked, or when its sport is in an EOI with no event
 * picked ("all events implied"). Estimates not given count 0; medals as
 * `eoiSportMedals`. Every count is over the organisations of the filter.
 */
export interface EntryAnalyticsKpis {
	/** NOC organisations in the filter. */
	organizations: number;
	eoi_started: number;
	eoi_final: number;
	ebn_started: number;
	ebn_final: number;
	/** Active sports of the games (in the sport filter). */
	sports_total: number;
	sports_with_interest: number;
	/** Active events of active disciplines (in the sport filter). */
	events_total: number;
	events_with_interest: number;
	est_athletes_men: number;
	est_athletes_women: number;
	est_officials: number;
	expected_gold: number;
	expected_silver: number;
	expected_bronze: number;
	medals_on_offer: EntryMedalsOnOffer;
	/** EBN: events entered (organisation × event rows), Σ numbers, Σ `impliedAthletes`. */
	ebn_events: number;
	ebn_entries: number;
	implied_athletes: number;
}

export interface EntryAnalyticsSport {
	sport: EntrySportRef;
	is_active: boolean;
	/** Organisations with the sport in their EOI. */
	nocs_interested: number;
	events_total: number;
	/** Events picked by at least one organisation, or implied (an EOI with no pick). */
	events_with_interest: number;
	est_athletes_men: number;
	est_athletes_women: number;
	est_officials: number;
	expected_gold: number;
	expected_silver: number;
	expected_bronze: number;
	medals_on_offer: EntryMedalsOnOffer;
	/** Organisations with an EBN row in the sport. */
	ebn_nocs: number;
	ebn_entries: number;
	implied_athletes: number;
}

export interface EntryAnalyticsNoc {
	organization: EntryOrgRef;
	eoi_state: EntryState;
	ebn_state: EntryState;
	eoi_submitted_at: string | null;
	ebn_submitted_at: string | null;
	/** EOI sports. */
	sports: number;
	/** Events picked, or implied by a sport with no pick. */
	events_with_interest: number;
	est_athletes_men: number;
	est_athletes_women: number;
	est_officials: number;
	expected_gold: number;
	expected_silver: number;
	expected_bronze: number;
	ebn_events: number;
	ebn_entries: number;
	implied_athletes: number;
}

/** One event of the picked sport (`?sport_id=`): interest against the quota. */
export interface EntryAnalyticsEvent {
	event_id: string;
	code: string;
	name: string;
	discipline: { discipline_id: string; code: string; name: string };
	gender: EventGender;
	competitor_type: CompetitorType;
	team_size_min: number | null;
	team_size_max: number | null;
	is_medal_event: boolean;
	gold_count: number;
	silver_count: number;
	bronze_count: number;
	max_entries_per_noc: number | null;
	max_shortlist_per_noc: number | null;
	/** Organisations that picked it, or whose EOI has the sport with no pick. */
	nocs_interested: number;
	/** Of those, the ones that picked it explicitly. */
	nocs_picked: number;
	/** Σ expected entries given on the picks. */
	est_entries: number;
	/** Σ of the quota over the interested organisations (`max_entries_per_noc` × nocs_interested); null = no quota. */
	entries_cap: number | null;
	expected_gold: number;
	expected_silver: number;
	expected_bronze: number;
	ebn_nocs: number;
	ebn_entries: number;
	implied_athletes: number;
}

/** EOI → EBN → Shortlist, in athletes, per organisation. */
export interface EntryAnalyticsFunnel {
	organization: EntryOrgRef;
	/** EOI: Σ est. athletes men + women (not given = 0). */
	eoi_athletes: number;
	/** EBN: Σ `impliedAthletes`. */
	ebn_athletes: number;
	/** Shortlist: APPROVED athletes with at least one PRIMARY row. */
	shortlisted: number;
}

/** Submissions per games-local day (the latest submission of each phase). */
export interface EntryAnalyticsDay {
	/** `YYYY-MM-DD` in the games' time zone. */
	date: string;
	eoi_submitted: number;
	ebn_submitted: number;
}

/**
 * `GET /entries/analytics` (admins) — ONE statement. NOT paginated, on
 * purpose: bounded by the NOCs (a dozen) × the programme (a few hundred
 * events), like `/medals/progress`. Never cached (live). Enum-like values
 * are in values (arrays), never keys.
 */
export interface EntryAnalytics {
	games_id: string;
	filters: { organization_ids: string[]; sport_id: string | null };
	kpis: EntryAnalyticsKpis;
	/** Every active sport (+ inactive ones with interest), over the organisation filter, never the sport filter. */
	by_sport: EntryAnalyticsSport[];
	by_noc: EntryAnalyticsNoc[];
	/** Only with `sport_id`: that sport's events (active, or with interest / entries). */
	by_event: EntryAnalyticsEvent[] | null;
	funnel: EntryAnalyticsFunnel[];
	timeline: EntryAnalyticsDay[];
}
